import PropTypes from "prop-types";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import {
  buildFlow,
  defaultStep,
  duplicateStep,
  hasFilters,
  insertStep,
  issueCodesOf,
  moveStep,
  removeStep,
  toApiSteps,
} from "../../../../utils/automation-builder";
import AddStep from "./AddStep";
import ExitCard from "./ExitCard";
import FirstEmailTip from "./FirstEmailTip";
import { DelayStep, EmailCard, FilterCard, StepMenu } from "./StepCards";
import { openModal, saveChanges } from "./save-changes";
import TriggerCard from "./TriggerCard";

const selectHref = (value) => `/actions/refresh?step=${value}`;
const selectStep = (value) =>
  submit("/actions/refresh?", { method: "GET", body: { step: value } });

const FlowCanvas = ({ detail, definition, options, selected, issues }) => {
  const { t } = useTranslation("coaching-portal");
  // The step being dragged, to mark it and show where it can be dropped
  const [dragging, setDragging] = useState(null);
  const { automation, steps, sharedWith } = detail;
  const id = automation._id;
  const apiSteps = toApiSteps(steps);
  const filterFields = definition.filterFields.map((field) => field.key);
  const nodes = buildFlow({ automation, steps: apiSteps });
  const hasTriggerFilter = hasFilters(automation.filters);

  const save = (next) => saveChanges(id, { steps: next });
  const tip = detail.suggestions?.find(
    (suggestion) => suggestion.code === "first_email_delay"
  );
  const firstEmail = apiSteps.findIndex((step) => step.type === "email");
  const tipOver = (node) =>
    tip && node.kind === "email" && node.index === tip.stepPosition ? (
      <FirstEmailTip
        suggestion={tip}
        onAddDelay={() =>
          save(
            insertStep(apiSteps, node.index, {
              type: "delay",
              config: { amount: 1, unit: "hours" },
            })
          )
        }
        onDismiss={() =>
          saveChanges(id, {
            dismissedTips: [
              ...(automation.dismissedTips ?? []),
              "firstEmailDelay",
            ],
          })
        }
      />
    ) : null;
  const addAt = (slot, type) => {
    if (type === "filter") {
      const target =
        slot === 0 && !hasTriggerFilter ? "trigger" : `new:${slot}`;
      openModal("automationFilter", { automation: id, target });
      return;
    }
    save(insertStep(apiSteps, slot, defaultStep(type)));
  };

  const stepMenu = (index) => {
    return (
      <StepMenu
        onEdit={() => selectStep(index)}
        onDuplicate={() => save(duplicateStep(apiSteps, index))}
        onDelete={() => save(removeStep(apiSteps, index))}
      />
    );
  };

  const triggerFilterMenu = (
    <StepMenu
      onEdit={() =>
        openModal("automationFilter", { automation: id, target: "trigger" })
      }
      onDelete={() => saveChanges(id, { filters: {} })}
    />
  );

  // The step that was picked up, or null when what is dropped is not one of ours
  const draggedStep = (event) => {
    const raw = event.dataTransfer.getData("text/plain");
    const from = Number(raw);
    return raw !== "" && Number.isInteger(from) && from < apiSteps.length
      ? from
      : null;
  };
  const dropAt = (slot) => (event) => {
    event.preventDefault();
    const from = draggedStep(event);
    setDragging(null);
    if (from !== null) save(moveStep(apiSteps, from, slot));
  };
  // Dropped on a card: the step takes that card's place
  const dropOnStep = (index) => (event) => {
    event.preventDefault();
    const from = draggedStep(event);
    setDragging(null);
    if (from !== null && from !== index) {
      save(moveStep(apiSteps, from, from < index ? index + 1 : index));
    }
  };
  const allowDrop = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  };

  const renderNode = (node) => {
    if (node.kind === "trigger") {
      return <TriggerCard definition={definition} sharedWith={sharedWith} />;
    }
    if (node.kind === "triggerFilter") {
      return (
        <FilterCard
          filters={automation.filters}
          fields={filterFields}
          options={options}
          menu={triggerFilterMenu}
          draggable={false}
        />
      );
    }
    if (node.kind === "exit") {
      return (
        <ExitCard
          automation={automation}
          selected={selected === "exit"}
          href={selectHref("exit")}
        />
      );
    }
    const isSelected = selected === String(node.index);
    if (node.kind === "email") {
      return (
        <EmailCard
          steps={apiSteps}
          index={node.index}
          selected={isSelected}
          menu={stepMenu(node.index)}
          href={selectHref(node.index)}
          issueCodes={issueCodesOf(issues, node.index)}
        />
      );
    }
    if (node.kind === "delay") {
      return (
        <DelayStep
          automationId={id}
          steps={apiSteps}
          index={node.index}
          selected={isSelected}
          menu={stepMenu(node.index)}
          href={selectHref(node.index)}
          quickOptions={
            Boolean(definition.systemEmail) && node.index + 1 === firstEmail
          }
        />
      );
    }
    return (
      <FilterCard
        filters={node.step.config.filters}
        fields={filterFields}
        options={options}
        menu={stepMenu(node.index)}
        selected={isSelected}
        href={selectHref(node.index)}
      />
    );
  };

  // The "+" goes after every node that can be followed by a step
  const addAfter = (node, position) => {
    if (node.kind === "exit") return false;
    if (
      node.kind === "trigger" &&
      nodes[position + 1]?.kind === "triggerFilter"
    ) {
      return false;
    }
    return true;
  };
  // The "+" that stays on show goes after the last card, before the exit rules
  const isLastStep = (position) =>
    nodes[position + 1] === undefined || nodes[position + 1].kind === "exit";
  const slotAfter = (node) => (node.kind === "trigger" ? 0 : node.slot);

  return (
    <ol className="ab-flow" data-dragging={dragging === null ? undefined : ""}>
      {nodes.map((node, position) => (
        <li key={node.key} className={`ab-node ab-node--${node.kind}`}>
          {tipOver(node)}
          {node.index === undefined ? (
            renderNode(node)
          ) : (
            // biome-ignore lint/a11y/noStaticElementInteractions: dragging is only a shortcut to move a step; the step menu keeps edit, duplicate and delete
            <div //NOSONAR
              role="presentation"
              className="ab-node__drag"
              draggable
              data-dragging={dragging === node.index ? "" : undefined}
              onDragStart={(event) => {
                event.dataTransfer.effectAllowed = "move";
                event.dataTransfer.setData("text/plain", String(node.index));
                // After the browser has taken the drag image: changing the page now can cancel the drag
                setTimeout(() => setDragging(node.index), 0);
              }}
              onDragEnd={() => setDragging(null)}
              onDragEnter={allowDrop}
              onDragOver={allowDrop}
              onDrop={dropOnStep(node.index)}
            >
              {renderNode(node)}
            </div>
          )}
          {addAfter(node, position) ? (
            <>
              <AddStep
                onAdd={(type) => addAt(slotAfter(node), type)}
                onDrop={dropAt(slotAfter(node))}
                last={isLastStep(position)}
                highlight={isLastStep(position) && apiSteps.length === 0}
              />
              {apiSteps.length === 0 && node.kind !== "exit" ? (
                <div className="ab-hint">
                  <strong>{t("emailAutomations.builder.add.hintTitle")}</strong>
                  <span>{t("emailAutomations.builder.add.hintBody")}</span>
                </div>
              ) : null}
            </>
          ) : null}
        </li>
      ))}
    </ol>
  );
};

FlowCanvas.propTypes = {
  detail: PropTypes.object.isRequired,
  definition: PropTypes.object.isRequired,
  options: PropTypes.object,
  selected: PropTypes.string,
  issues: PropTypes.array,
};

export default FlowCanvas;
